import { onBeforeUnmount, type Ref } from 'vue'

// Resolves CSS lengths (`4px`, `1rem`, `var(--radius-xs)`, `50%`) to pixel
// numbers for SVG path math. Writes the value to a hidden div inside the
// wrapper and reads `offsetWidth` — the div inherits the wrapper's CSS scope,
// so var() / rem / % resolve naturally without us parsing anything.
export function useResolveCssSize(wrapperRef: Ref<HTMLElement | null>) {
  let hiddenDiv: HTMLDivElement | null = null

  function ensureHiddenDiv(): HTMLDivElement | null {
    if (hiddenDiv || !wrapperRef.value) return hiddenDiv
    hiddenDiv = document.createElement('div')
    hiddenDiv.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;height:0;width:0;'
    wrapperRef.value.appendChild(hiddenDiv)
    return hiddenDiv
  }

  function resolvePx(cssValue: string | undefined, fallback = 0): number {
    if (!cssValue) return fallback
    const pxMatch = cssValue.match(/^(-?[0-9.]+)px$/)
    if (pxMatch) return parseFloat(pxMatch[1]!)
    const div = ensureHiddenDiv()
    if (!div) return fallback
    div.style.width = cssValue
    const measured = div.offsetWidth
    return Number.isFinite(measured) ? measured : fallback
  }

  onBeforeUnmount(() => {
    if (hiddenDiv?.parentNode) hiddenDiv.parentNode.removeChild(hiddenDiv)
    hiddenDiv = null
  })

  return { resolvePx }
}
